import React, { useEffect, useState } from "react";
import { Tab, TabList } from "@fluentui/react-components";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next"; 
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { SIGNALR_PATH } from "../../Config/globalParams";
import "./Dashboard.css";
import {
  getMyAgentProfile,
  getScopes,
  getQueue,
  getGroups,
  getCallFlows,
} from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import QueuesContainer from "../../Containers/DashboardContainer/Body/QueuesContainer";
import PresenceContainer from "../../Containers/PresenceContainer";
import AgentsContainer from "../../Containers/DashboardContainer/Body/AgentsContainer";
import HeaderContainer from "../../Containers/DashboardContainer/Header/HeaderContainer";
import PresenceProfileContainer from "../../Containers/DashboardContainer/Header/PresenceProfileContainer";

const Dashboard = ({
  setMyAgentProfile,
  setScope,
  setQueues,
  setGroups,
  setCallFlows,
  setSupervisedAgentIds,
  setAgentIds,
  setSupervisedAgentDetails,
}) => {
  const authContext = useMsal();
    const { t } = useTranslation(); 
  let accounts = authContext.accounts;
  let instance = authContext.instance;
  const activeAccount = accounts[0];
  const tenantId = activeAccount.tenantId;
  const userId = activeAccount.localAccountId;
  const [signalRConnexion, setSignalRConnexion] = useState(null);
  const [selectedTab, setSelectedTab] = useState("queues");
  //signalr connexion
  const createSignalRConnection = () => {
    //  Define URL
    const urlRoot = SIGNALR_PATH + "?tenant=t_" + tenantId.replace(/-/g, "")
    // Build Connection
    const connection = new HubConnectionBuilder()
      .withUrl(urlRoot)
      .configureLogging(LogLevel.Warning)
      .build();
    // Start Connection
    const startConnection = () => {
      connection
        .start({
          transport: [
            "webSockets",
            "longPolling",
            "foreverFrame",
            "serverSentEvents",
          ],
        })
        .then(() => {
          setSignalRConnexion(connection); // Save connection in state
        })
        .catch((err) => {
          console.error("SignalR Connection Error: ", err);
          setTimeout(() => startConnection(), 5000); // Retry after 5 seconds
        });
    };

    startConnection(); // Initiate the connection
  };

  // récupérer les scopes
  const fetchScopes = (tenant, token) => {
    return new Promise((resolve) => {
      getScopes(tenant, token)
        .then((scopes) => {
          const scopeNames = Object.values(scopes).map((scope) => scope.RowKey);

          setScope(scopeNames);

          resolve(scopeNames);
        })
        .catch((error) => {
          console.error("Erreur lors de la gestion des scopes :", error);

          resolve([]);
        });
    });
  };

  // Fonction principale pour récupérer les données
  const fetchData = () => {
    getAccessToken(instance, activeAccount)
      .then((token) => {
        getMyAgentProfile(tenantId, userId, token)
          .then((agentProfile) => {
            setMyAgentProfile(agentProfile);

            return fetchScopes(tenantId, token);
          })
          .then((scopeNames) => {
            if (scopeNames.length > 0) {
              let queuesPromises = [];
              let groupsPromises = [];
              let callflowsPromises = [];

              for (const scopeName of scopeNames) {
                queuesPromises.push(
                  getQueue(tenantId, scopeName, "ALL", userId)
                );
                callflowsPromises.push(
                  getCallFlows(tenantId, scopeName, userId)
                );
                groupsPromises.push(
                  getGroups(tenantId, scopeName, "ALL", userId)
                );
              }

              Promise.all(queuesPromises).then((queuesPromisesResults) => {
                const queues = Object.fromEntries(
                  queuesPromisesResults.flatMap((result) =>
                    Object.entries(result)
                  ) //Convertir chaque objet en paires clé-valeur
                );
                setQueues(queues);
                // Extraire les ids d'agents et les mettre à jour dans l'état
                let newAgentIds = [];
                Object.values(queues).forEach((queue) => {
                  const agentsDetails = queue.agentDetails || {};
                  newAgentIds = [...newAgentIds, ...Object.keys(agentsDetails)];
                });
                setAgentIds(newAgentIds);
              });
              Promise.all(groupsPromises).then((groupsPromisesResults) => {
                const groups = Object.fromEntries(
                  groupsPromisesResults.flat().map((group) => [group.id, group])
                );
                const supervisedAgentIds = []; // Tableau pour stocker les IDs des agents
                const supervisedAgentDetails = {};
                Object.values(groups).forEach((group) => {
                  if (group.agentsIds.includes(userId)) {
                    // Récupérer les détails des agents pour le groupe
                    const agentDetails = group.agentDetails || {};
                    if (agentDetails[userId]?.isSupervisor) {
                      // Parcourir les détails des agents dans le groupe
                      Object.entries(agentDetails).forEach(
                        ([agentId, agent]) => {
                          // Ajouter uniquement les IDs des agents supervisés et des superviseurs
                          supervisedAgentIds.push(agentId);
                          supervisedAgentDetails[agentId] =
                            agentDetails[agentId];
                        }
                      );
                    }
                  }
                });
                setGroups(groups);
                setSupervisedAgentIds(supervisedAgentIds); // Mettre à jour avec les IDs des superviseurs et des agents
                setSupervisedAgentDetails(supervisedAgentDetails);
              });

              Promise.all(callflowsPromises).then(
                (callflowsPromisesResults) => {
                  const callFlows = Object.fromEntries(
                    callflowsPromisesResults.flatMap((result) =>
                      Object.entries(result)
                    ) //Convertir chaque objet en paires clé-valeur
                  );
                  setCallFlows(callFlows);
                }
              );
            }
          });
      })

      .catch((error) => {
        console.error("Erreur lors de la récupération des données :", error);
      });
  };

  useEffect(() => {
    createSignalRConnection();
    if (instance) {
      fetchData();
    }
  }, [instance]);

  return (
    <div className="dashboard-container">
      <HeaderContainer />
      <TabList
        selectedValue={selectedTab}
        onTabSelect={(e, data) => setSelectedTab(data.value)}
        className="tab-list"
       
      //  appearance="subtle-circular" 
      >
        <Tab  
          value="queues"
          className={`tab  ${selectedTab === "queues" ? "selected" : ""}`}
        >
         {t("QUEUE")}
        </Tab>
        <Tab  
          value="agents"
          className={`tab  ${selectedTab === "agents" ? "selected" : ""}`}
        >
           {t("AGENT")}
        </Tab>
      </TabList>

      {selectedTab === "queues" && (
        <QueuesContainer  signalRConnection={signalRConnexion} />
      )}
      {selectedTab === "agents" && (
        <AgentsContainer signalRConnection={signalRConnexion} />
      )}
      <PresenceProfileContainer/>
      <PresenceContainer />
    </div>
  );
};

export default Dashboard;
